import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Consultation/Booking APIs
export const bookConsultation = async (bookingData) => {
  const response = await axios.post(`${API}/consultations/book`, bookingData);
  return response.data;
};

// ---- Admin slot management (X-Admin-Key header required) ----
const adminHeaders = (adminKey) => ({ headers: { 'X-Admin-Key': adminKey } });

export const adminGetSlotsByDate = async (adminKey, date) => {
  const response = await axios.get(`${API}/consultations/slots/admin/by-date?date=${date}`, adminHeaders(adminKey));
  return response.data;
};

export const adminToggleSlot = async (adminKey, slotId, available) => {
  const response = await axios.patch(
    `${API}/consultations/slots/${slotId}`,
    { available },
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminBlockDay = async (adminKey, date, blocked) => {
  const response = await axios.post(
    `${API}/consultations/slots/block-day`,
    { date, blocked },
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminGetUpcomingBookings = async (adminKey, limit = 20) => {
  const response = await axios.get(
    `${API}/consultations/slots/admin/upcoming-bookings?limit=${limit}`,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminGetSettings = async (adminKey) => {
  const response = await axios.get(`${API}/config/settings`, adminHeaders(adminKey));
  return response.data;
};

export const adminUpdateSettings = async (adminKey, payload) => {
  const response = await axios.put(`${API}/config/settings`, payload, adminHeaders(adminKey));
  return response.data;
};

export const adminResetDemoAccount = async (adminKey) => {
  const response = await axios.post(`${API}/admin/demo/reset`, {}, adminHeaders(adminKey));
  return response.data;
};

export const adminDeleteConsultation = async (adminKey, consultationId) => {
  const response = await axios.delete(
    `${API}/consultations/booking/${consultationId}`,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const getAvailableSlots = async () => {
  const response = await axios.get(`${API}/consultations/slots/available`);
  return response.data;
};

export const getSlotsByDate = async (date) => {
  const response = await axios.get(`${API}/consultations/slots?date=${date}`);
  return response.data;
};

// Contact Form API
export const submitContact = async (contactData) => {
  const response = await axios.post(`${API}/contacts/`, contactData);
  return response.data;
};

// Programs API
export const getPrograms = async () => {
  const response = await axios.get(`${API}/programs/`);
  return response.data;
};

export const getProgramById = async (programId) => {
  const response = await axios.get(`${API}/programs/${programId}`);
  return response.data;
};

export const getStandaloneConsultation = async () => {
  const response = await axios.get(`${API}/programs/consultation`);
  return response.data;
};

// Blog Posts API
export const getBlogPosts = async () => {
  const response = await axios.get(`${API}/blog/posts`);
  return response.data;
};

export const getBlogPostById = async (postId) => {
  const response = await axios.get(`${API}/blog/posts/${postId}`);
  return response.data;
};

export const getBlogPostBySlug = async (slug) => {
  const response = await axios.get(`${API}/blog/posts/by-slug/${encodeURIComponent(slug)}`);
  return response.data;
};

// Admin Blog CRUD (X-Admin-Key header required)
export const adminListBlogPosts = async (adminKey) => {
  const response = await axios.get(`${API}/admin/blog/posts`, adminHeaders(adminKey));
  return response.data;
};

export const adminReorderBlogPosts = async (adminKey, orderedIds) => {
  const response = await axios.post(
    `${API}/admin/blog/posts/reorder`,
    { ordered_ids: orderedIds },
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminGetBlogPost = async (adminKey, postIdOrSlug) => {
  const response = await axios.get(
    `${API}/admin/blog/posts/${encodeURIComponent(postIdOrSlug)}`,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminUpdateBlogPost = async (adminKey, postIdOrSlug, payload) => {
  const response = await axios.put(
    `${API}/admin/blog/posts/${encodeURIComponent(postIdOrSlug)}`,
    payload,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminCreateBlogPost = async (adminKey, payload) => {
  const response = await axios.post(
    `${API}/admin/blog/posts`,
    payload,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminAiTitleSuggestions = async (adminKey, payload) => {
  const response = await axios.post(`${API}/admin/blog/ai/titles`, payload, adminHeaders(adminKey));
  return response.data;
};

export const adminAiSeoCheck = async (adminKey, payload) => {
  const response = await axios.post(`${API}/admin/blog/ai/seo-check`, payload, adminHeaders(adminKey));
  return response.data;
};

export const adminAiCleanup = async (adminKey, payload) => {
  const response = await axios.post(`${API}/admin/blog/ai/cleanup`, payload, { ...adminHeaders(adminKey), timeout: 180000 });
  return response.data;
};

export const adminAiProofread = async (adminKey, payload) => {
  const response = await axios.post(`${API}/admin/blog/ai/proofread`, payload, { ...adminHeaders(adminKey), timeout: 180000 });
  return response.data;
};

export const adminAiHumanize = async (adminKey, payload) => {
  const response = await axios.post(`${API}/admin/blog/ai/humanize`, payload, { ...adminHeaders(adminKey), timeout: 240000 });
  return response.data;
};

// Hosszú cikkeknél a szinkron hívást az ingress elvágná, ezért háttérfeladat + pollozás.
export const adminAiHumanizeJob = async (adminKey, payload, { onTick } = {}) => {
  const { job_id } = (await axios.post(
    `${API}/admin/blog/ai/humanize/start`, payload, adminHeaders(adminKey),
  )).data;
  const deadline = Date.now() + 6 * 60 * 1000;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    await new Promise((r) => setTimeout(r, 4000));
    const job = (await axios.get(`${API}/admin/blog/ai/humanize/job/${job_id}`, adminHeaders(adminKey))).data;
    if (job.status === 'done') return { content: job.content, excerpt: job.excerpt };
    if (job.status === 'error') throw new Error(job.error || 'Az emberibbé tétel nem sikerült.');
    if (Date.now() > deadline) throw new Error('Az átírás túl sokáig tartott. Próbáld újra.');
    if (onTick) onTick();
  }
};

export const adminHumanizeApply = async (adminKey, postId, payload) => {
  const response = await axios.post(`${API}/admin/blog/posts/${postId}/humanize-apply`, payload, adminHeaders(adminKey));
  return response.data;
};

export const adminHumanizeRevert = async (adminKey, postId) => {
  const response = await axios.post(`${API}/admin/blog/posts/${postId}/humanize-revert`, {}, adminHeaders(adminKey));
  return response.data;
};

export const adminHumanizeBatchStart = async (adminKey, scope = 'unhumanized') => {
  const response = await axios.post(`${API}/admin/blog/ai/humanize/batch/start`, { scope }, adminHeaders(adminKey));
  return response.data;
};

export const adminHumanizeBatchLatest = async (adminKey) => {
  const response = await axios.get(`${API}/admin/blog/ai/humanize/batch/latest`, adminHeaders(adminKey));
  return response.data;
};

export const adminHumanizeBatchItem = async (adminKey, jobId, postId) => {
  const response = await axios.get(`${API}/admin/blog/ai/humanize/batch/${jobId}/item/${postId}`, adminHeaders(adminKey));
  return response.data;
};

export const adminHumanizeBatchDecide = async (adminKey, jobId, postId, action) => {
  const response = await axios.post(
    `${API}/admin/blog/ai/humanize/batch/${jobId}/item/${postId}/decide`, { action }, adminHeaders(adminKey),
  );
  return response.data;
};

export const adminStyleCheck = async (adminKey, slug) => {
  const url = slug ? `${API}/admin/blog/style-check/${slug}` : `${API}/admin/blog/style-check`;
  return (await axios.get(url, adminHeaders(adminKey))).data;
};

export const adminStyleVerify = async (adminKey, slug, findingId, quote = '') =>
  (await axios.post(`${API}/admin/blog/style-check/${slug}/verify`,
    { finding_id: findingId, quote }, adminHeaders(adminKey))).data;

export const adminStyleDismiss = async (adminKey, slug, findingId, quote = '') =>
  (await axios.post(`${API}/admin/blog/style-check/${slug}/dismiss`,
    { finding_id: findingId, quote }, adminHeaders(adminKey))).data;

export const adminStyleSuggest = async (adminKey, payload) =>
  (await axios.post(`${API}/admin/blog/style-check/suggest`, payload,
    { ...adminHeaders(adminKey), timeout: 90000 })).data;

export const adminIndexNowSubmit = async (adminKey, slugs = []) =>
  (await axios.post(`${API}/admin/blog/indexnow/submit`, { slugs },
    { ...adminHeaders(adminKey), timeout: 60000 })).data;

export const adminSitemapResubmit = async (adminKey) =>
  (await axios.post(`${API}/admin/blog/sitemap/resubmit`, {},
    { ...adminHeaders(adminKey), timeout: 90000 })).data;

export const adminAnnouncementBarGet = async (adminKey) =>
  (await axios.get(`${API}/admin/announcement-bar`, adminHeaders(adminKey))).data;

export const adminAnnouncementBarSave = async (adminKey, payload) =>
  (await axios.put(`${API}/admin/announcement-bar`, payload, adminHeaders(adminKey))).data;

export const requestMealSample = async (payload) =>
  (await axios.post(`${API}/meal-sample/request`, payload, { timeout: 60000 })).data;

export const adminNewsletterStatus = async (adminKey) => {
  const response = await axios.get(`${API}/admin/newsletter/status`, adminHeaders(adminKey));
  return response.data;
};

export const adminNewsletterRun = async (adminKey, { force = false, dryRun = false } = {}) => {
  const response = await axios.post(
    `${API}/admin/newsletter/run?force=${force}&dry_run=${dryRun}`,
    {},
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminLeads = async (adminKey) => {
  const response = await axios.get(`${API}/admin/leads`, adminHeaders(adminKey));
  return response.data;
};

export const adminNewsletterQueue = async (adminKey) => {
  const response = await axios.get(`${API}/admin/newsletter/queue`, adminHeaders(adminKey));
  return response.data;
};

export const adminNewsletterQueueSave = async (adminKey, slugs) => {
  const response = await axios.put(`${API}/admin/newsletter/queue`, { slugs }, adminHeaders(adminKey));
  return response.data;
};

// ---- Training plans (admin) ----
export const adminTrainingUsers = async (adminKey) =>
  (await axios.get(`${API}/admin/training/users`, adminHeaders(adminKey))).data;
export const adminTrainingPlans = async (adminKey) =>
  (await axios.get(`${API}/admin/training/plans`, adminHeaders(adminKey))).data;
export const adminTrainingPlanCreate = async (adminKey, payload) =>
  (await axios.post(`${API}/admin/training/plans`, payload, adminHeaders(adminKey))).data;
export const adminTrainingPlanGet = async (adminKey, planId) =>
  (await axios.get(`${API}/admin/training/plans/${planId}`, adminHeaders(adminKey))).data;
export const adminTrainingPlanUpdate = async (adminKey, planId, payload) =>
  (await axios.put(`${API}/admin/training/plans/${planId}`, payload, adminHeaders(adminKey))).data;
export const adminTrainingPlanStatus = async (adminKey, planId, status) =>
  (await axios.post(`${API}/admin/training/plans/${planId}/status`, { status }, adminHeaders(adminKey))).data;
export const adminTrainingPlanDelete = async (adminKey, planId) =>
  (await axios.delete(`${API}/admin/training/plans/${planId}`, adminHeaders(adminKey))).data;
export const adminClientProgress = async (adminKey, userId) =>
  (await axios.get(`${API}/admin/training/clients/${userId}/progress`, adminHeaders(adminKey))).data;
export const adminClientProfileUpdate = async (adminKey, userId, payload) =>
  (await axios.patch(`${API}/admin/training/clients/${userId}/profile`, payload, adminHeaders(adminKey))).data;
export const adminClientAddMeasurement = async (adminKey, userId, payload) =>
  (await axios.post(`${API}/admin/training/clients/${userId}/measurements`, payload, adminHeaders(adminKey))).data;
export const adminClientAddWorkoutLog = async (adminKey, userId, payload) =>
  (await axios.post(`${API}/admin/training/clients/${userId}/workout-logs`, payload, adminHeaders(adminKey))).data;

export const adminDeleteBlogPost = async (adminKey, postIdOrSlug) => {
  const response = await axios.delete(
    `${API}/admin/blog/posts/${encodeURIComponent(postIdOrSlug)}`,
    adminHeaders(adminKey),
  );
  return response.data;
};

// Lead Magnet API
export const subscribeLeadMagnet = async (data) => {
  const response = await axios.post(`${API}/subscribers/lead-magnet`, data);
  return response.data;
};

// Calculator API
export const calculateBodyMetrics = async (data) => {
  const response = await axios.post(`${API}/calculator/`, data);
  return response.data;
};

export const saveCalculatorResults = async (data) => {
  const response = await axios.post(`${API}/calculator/save-results`, data);
  return response.data;
};

// Aesthetics (Adonis & Venus Index) API
export const calculateAesthetics = async (data) => {
  const response = await axios.post(`${API}/calculator/aesthetics`, data);
  return response.data;
};

export const saveAestheticsResults = async (data) => {
  const response = await axios.post(`${API}/calculator/aesthetics/save-results`, data);
  return response.data;
};

// Phase-2 Macronutrient Calculator API
export const calculateMacros = async (data) => {
  const response = await axios.post(`${API}/calculator/macros`, data);
  return response.data;
};

// Caliper (skinfold) body-fat calculator (Jackson-Pollock JP-3 / JP-7)
export const calculateCaliper = async (data) => {
  const response = await axios.post(`${API}/calculator/caliper`, data);
  return response.data;
};

// Sends the personalized macro plan to the user via email.
// data: { email, name?, source: 'manual'|'auto', macros: MacrosResponse, weight_kg }
export const sendMacroPlanEmail = async (data) => {
  const response = await axios.post(`${API}/calculator/macros/email`, data);
  return response.data;
};

// Admin Compliance dashboard
export const adminGetCompliance = async (adminKey, limit = 30) => {
  const response = await axios.get(`${API}/admin/compliance/`, {
    params:  { limit },
    headers: { 'X-Admin-Key': adminKey },
  });
  return response.data;
};

// Admin Compliance — Audit Pack ZIP (binary blob download)
export const adminFetchAuditPack = async (adminKey) => {
  const response = await axios.get(`${API}/admin/compliance/audit-pack`, {
    responseType: 'blob',
    headers:      { 'X-Admin-Key': adminKey },
  });
  return response.data;  // Blob
};

// Admin user (test account) management
export const adminUsersList = async (adminKey) =>
  (await axios.get(`${API}/admin/users`, adminHeaders(adminKey))).data;
export const adminUserCreate = async (adminKey, payload) =>
  (await axios.post(`${API}/admin/users`, payload, adminHeaders(adminKey))).data;
export const adminUserInvite = async (adminKey, payload) =>
  (await axios.post(`${API}/admin/users/invite`, payload, adminHeaders(adminKey))).data;
export const adminUserDelete = async (adminKey, email) =>
  (await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, adminHeaders(adminKey))).data;
export const adminUserUpdateSettings = async (adminKey, userId, payload) =>
  (await axios.patch(`${API}/admin/users/${userId}/settings`, payload, adminHeaders(adminKey))).data;
export const adminClientReportSend = async (adminKey, userId, payload = {}) =>
  (await axios.post(`${API}/admin/client-report/${userId}/send`, payload, adminHeaders(adminKey))).data;

// Portal feedback + admin feedback management
export const portalFeedbackSend = async (payload) =>
  (await axios.post(`${API}/portal/feedback`, payload, { withCredentials: true, ...authHeaders() })).data;
export const adminFeedbackList = async (adminKey) =>
  (await axios.get(`${API}/admin/feedback`, adminHeaders(adminKey))).data;
export const adminFeedbackStatus = async (adminKey, id, status) =>
  (await axios.patch(`${API}/admin/feedback/${id}`, { status }, adminHeaders(adminKey))).data;
export const adminFeedbackDelete = async (adminKey, id) =>
  (await axios.delete(`${API}/admin/feedback/${id}`, adminHeaders(adminKey))).data;

// Admin contacts (chat widget + contact form submissions)
export const adminContactsUnreadCount = async (adminKey) =>
  (await axios.get(`${API}/admin/contacts/unread-count`, adminHeaders(adminKey))).data;
export const adminContactsList = async (adminKey, limit = 200) =>
  (await axios.get(`${API}/admin/contacts?limit=${limit}`, adminHeaders(adminKey))).data;
export const adminContactMarkRead = async (adminKey, id) =>
  (await axios.post(`${API}/admin/contacts/${id}/read`, {}, adminHeaders(adminKey))).data;
export const adminContactsMarkAllRead = async (adminKey) =>
  (await axios.post(`${API}/admin/contacts/mark-all-read`, {}, adminHeaders(adminKey))).data;

// Coach ↔ client messages (check-in + napló kommentek)
export const adminClientMessages = async (adminKey, userId) =>
  (await axios.get(`${API}/admin/training/clients/${userId}/messages`, adminHeaders(adminKey))).data;
export const adminClientMessageSend = async (adminKey, userId, payload) =>
  (await axios.post(`${API}/admin/training/clients/${userId}/messages`, payload, adminHeaders(adminKey))).data;
export const portalCoachMessages = async () =>
  (await axios.get(`${API}/portal/coach-messages`, { withCredentials: true, ...authHeaders() })).data;
export const portalCoachMessageSend = async (message) =>
  (await axios.post(`${API}/portal/coach-messages`, { message }, { withCredentials: true, ...authHeaders() })).data;

// Payment (Barion + Számlázz.hu) API
export const startPayment = async (data) => {
  const response = await axios.post(`${API}/checkout/start`, data);
  return response.data;
};

// Bank transfer checkout (used while Barion is pending; will coexist after)
export const startBankTransfer = async (data) => {
  const response = await axios.post(`${API}/checkout/bank-transfer/start`, data);
  return response.data;
};

// ---- Auth (Google session cookie + JWT email/password) ----
const authHeaders = () => {
  const jwt = localStorage.getItem('oc_access_token');
  return jwt ? { headers: { Authorization: `Bearer ${jwt}` } } : {};
};

export const authGoogleCallback = async (session_id) => {
  const response = await axios.post(
    `${API}/auth/google/callback`,
    { session_id },
    { withCredentials: true },
  );
  return response.data;
};

export const authMe = async () => {
  const response = await axios.get(`${API}/auth/me`, {
    withCredentials: true,
    ...authHeaders(),
  });
  return response.data;
};

export const authEmailLogin = async (email, password) => {
  const response = await axios.post(`${API}/auth/email/login`, { email, password });
  return response.data;
};

export const authProbeSetPasswordToken = async (token) => {
  const response = await axios.get(`${API}/auth/email/set-password/${encodeURIComponent(token)}`);
  return response.data;
};

export const authSetPassword = async (token, password) => {
  const response = await axios.post(`${API}/auth/email/set-password`, { token, password });
  return response.data;
};

export const authLogout = async () => {
  const response = await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
  return response.data;
};

// ---- Client portal ----
export const portalMe = async () => {
  const response = await axios.get(`${API}/portal/me`, {
    withCredentials: true,
    ...authHeaders(),
  });
  return response.data;
};

// Bank transfer checkout with optional coupon code (Phase 3)
export const startBankTransferWithCoupon = async (data, couponCode) => {
  const payload = { ...data };
  const code = couponCode ? couponCode.trim().toUpperCase() : '';
  if (code) payload.coupon_code = code;
  const response = await axios.post(`${API}/checkout/bank-transfer/start`, payload);
  return response.data;
};

export const portalAvailableSlots = async (days = 21) => {
  const response = await axios.get(`${API}/portal/available-slots?days=${days}`, {
    withCredentials: true,
    ...authHeaders(),
  });
  return response.data;
};

export const portalCreateBooking = async (slotDate, slotTime, note) => {
  const response = await axios.post(`${API}/portal/bookings`, {
    slot_date: slotDate, slot_time: slotTime, note: note || undefined,
  }, { withCredentials: true, ...authHeaders() });
  return response.data;
};

export const portalCancelBooking = async (bookingId) => {
  const response = await axios.delete(`${API}/portal/bookings/${encodeURIComponent(bookingId)}`, {
    withCredentials: true, ...authHeaders(),
  });
  return response.data;
};

export const portalFreezePass = async (passId, days) => {
  const response = await axios.post(`${API}/portal/passes/${encodeURIComponent(passId)}/freeze`, {
    days,
  }, { withCredentials: true, ...authHeaders() });
  return response.data;
};

// ---- Admin bookings ----
export const adminListBookings = async (adminKey, { status = '', upcoming = false } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (upcoming) qs.set('upcoming', 'true');
  const url = `${API}/admin/bookings${qs.toString() ? '?' + qs.toString() : ''}`;
  const response = await axios.get(url, adminHeaders(adminKey));
  return response.data;
};

export const adminListConsultationOrders = async (adminKey) => {
  const response = await axios.get(`${API}/admin/consultation-orders`, adminHeaders(adminKey));
  return response.data;
};

export const adminSetConsultationFulfilled = async (adminKey, orderId, fulfilled) => {
  const response = await axios.post(
    `${API}/admin/consultation-orders/${encodeURIComponent(orderId)}/fulfill`,
    { fulfilled },
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminMarkAttended = async (adminKey, bookingId) => {
  const response = await axios.post(
    `${API}/admin/bookings/${encodeURIComponent(bookingId)}/attended`, null,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminMarkNoShow = async (adminKey, bookingId) => {
  const response = await axios.post(
    `${API}/admin/bookings/${encodeURIComponent(bookingId)}/no-show`, null,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminAdminCancelBooking = async (adminKey, bookingId, reason = '') => {
  const response = await axios.post(
    `${API}/admin/bookings/${encodeURIComponent(bookingId)}/cancel?reason=${encodeURIComponent(reason)}`, null,
    adminHeaders(adminKey),
  );
  return response.data;
};

// ---- Admin bank transfers ----
export const adminListBankTransfers = async (adminKey) => {
  const response = await axios.get(`${API}/admin/bank-transfers`, adminHeaders(adminKey));
  return response.data;
};

export const adminConfirmBankTransfer = async (adminKey, orderId) => {
  const response = await axios.post(
    `${API}/admin/bank-transfers/${encodeURIComponent(orderId)}/confirm`,
    null,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const adminCancelBankTransfer = async (adminKey, orderId, reason = '') => {
  const response = await axios.post(
    `${API}/admin/bank-transfers/${encodeURIComponent(orderId)}/cancel?reason=${encodeURIComponent(reason)}`,
    null,
    adminHeaders(adminKey),
  );
  return response.data;
};

export const getPaymentStatus = async (orderId) => {
  const response = await axios.get(`${API}/checkout/status/${orderId}`);
  return response.data;
};

// ---- Admin retention overview ----
export const adminRetentionOverview = async (adminKey) => {
  const response = await axios.get(`${API}/admin/retention/overview`, adminHeaders(adminKey));
  return response.data;
};

export const adminRetentionRemind = async (adminKey, { passId, channel, subject, message }) => {
  const response = await axios.post(
    `${API}/admin/retention/remind`,
    { pass_id: passId, channel, subject, message },
    adminHeaders(adminKey),
  );
  return response.data;
};

// ---- Admin smoke test cleanup ----
export const adminSmokeTestStatus = async (adminKey) => {
  const response = await axios.get(`${API}/admin/smoke-test/status`, adminHeaders(adminKey));
  return response.data;
};

export const adminSmokeTestCleanupPayments = async (adminKey) => {
  const response = await axios.delete(
    `${API}/admin/smoke-test/cleanup?drop_payments=true&keep_program=true`,
    adminHeaders(adminKey),
  );
  return response.data;
};

// ---- Training (portal) ----
export const portalTraining = async () =>
  (await axios.get(`${API}/portal/training`, { withCredentials: true, ...authHeaders() })).data;
export const portalAddMeasurement = async (payload) =>
  (await axios.post(`${API}/portal/measurements`, payload, { withCredentials: true, ...authHeaders() })).data;
export const portalAddWorkoutLog = async (payload) =>
  (await axios.post(`${API}/portal/workout-logs`, payload, { withCredentials: true, ...authHeaders() })).data;

export const portalGdprExport = async () =>
  (await axios.get(`${API}/gdpr/my-export`, { withCredentials: true, ...authHeaders() })).data;

// ---------------- Custom offers (personalized payment links) ---------------
export const adminCustomOfferCreate = async (adminKey, payload) =>
  (await axios.post(`${API}/admin/custom-offers`, payload, adminHeaders(adminKey))).data;
export const adminCustomOfferList = async (adminKey) =>
  (await axios.get(`${API}/admin/custom-offers`, adminHeaders(adminKey))).data;
export const adminCustomOfferRevoke = async (adminKey, offerId) =>
  (await axios.post(`${API}/admin/custom-offers/${offerId}/revoke`, {}, adminHeaders(adminKey))).data;
export const adminCustomOfferSendEmail = async (adminKey, offerId) =>
  (await axios.post(`${API}/admin/custom-offers/${offerId}/send-email`, {}, adminHeaders(adminKey))).data;

export const publicCustomOfferGet = async (token) =>
  (await axios.get(`${API}/custom-offers/${token}`)).data;
export const publicCustomOfferStartPay = async (token, billing) =>
  (await axios.post(`${API}/checkout/custom-offer/${token}/start`, billing)).data;

// ---------------- Blog view analytics --------------------------------------
export const blogViewStart = async (payload) =>
  (await axios.post(`${API}/blog/view/start`, payload)).data;
export const blogViewBeat = async (viewId, payload) =>
  (await axios.post(`${API}/blog/view/${viewId}/beat`, payload)).data;
export const blogShare = async (payload) =>
  (await axios.post(`${API}/blog/share`, payload)).data;
export const adminBlogAnalytics = async (adminKey, days = 30) =>
  (await axios.get(`${API}/admin/blog/analytics?days=${days}`, adminHeaders(adminKey))).data;

// Calculator saves (user-scoped, JWT-authed)
export const portalCalculatorSave = async (payload) =>
  (await axios.post(`${API}/portal/calculator-saves`, payload, { withCredentials: true, ...authHeaders() })).data;
export const portalCalculatorSavesList = async (type) => {
  const qs = type ? `?type=${encodeURIComponent(type)}` : '';
  return (await axios.get(`${API}/portal/calculator-saves${qs}`, { withCredentials: true, ...authHeaders() })).data;
};
export const portalCalculatorSaveDelete = async (id) =>
  (await axios.delete(`${API}/portal/calculator-saves/${id}`, { withCredentials: true, ...authHeaders() })).data;
